import { MessageType } from './message-type';
import { Message } from './message';
import { PostMessageBridgeFactory } from './post-message-bridge-factory';
import { EventEmitter, NgZone } from '@angular/core';
import { PostMessageBridgeInterface } from './post-message-bridge.interface';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { StatusMessageInterface } from './status-message.interface';

export const DEFAULT_BRIDGE_NAME = 'tab-messages';
export class TabSocket {

    onClose: EventEmitter<any> = new EventEmitter();

    private bridge: PostMessageBridgeInterface | null;
    private pollingInterval: null | any = null;
    private messageSubject$ = new Subject<Message<any>>();
    private statusPushSubject$ = new BehaviorSubject<StatusMessageInterface | null>(null);

    constructor(
        zone: NgZone,
        private targetWindow: Window,
        private name = DEFAULT_BRIDGE_NAME
    ) {
        this.bridge = new PostMessageBridgeFactory(zone).makeInstance();
        this.bridge.setEnableLogging(false);
        const currentWindow: Window = window;

        this.bridge
            .connect(currentWindow, targetWindow)
            .makeBridge(name);

        if (targetWindow?.opener === window || targetWindow?.parent === window) {
            // On close event, post a message to peer
            window.addEventListener('unload', this.closeChild);
            this.pollingInterval = setInterval(() => {
                if (this.targetWindow.closed) {
                    clearInterval(this.pollingInterval);
                    this.onClose.emit();
                }
            }, 1000);
        }
        this.listen();
    }

    send<TMessageType extends MessageType = MessageType>(
        data: any,
        messageType?: TMessageType,
        uuid: string = ''
    ) {
        this.bridge?.sendMessage(this.name, new Message(data, messageType, uuid));
    }

    sendWithConfirmAsync<TRequest, TResponse, TMessageType extends MessageType = MessageType>(
        message: TRequest,
        messageType?: TMessageType,
        timeout = 5000
    ): Promise<Message<TResponse>> {

        const messageUUID: string | undefined = this.bridge?.createUUID();
        this.bridge?.sendMessage(this.name, new Message<TRequest>(message, messageType, messageUUID));
        return new Promise<Message<TResponse>>((resolve, reject) => {
            const confirmListener = (ack: Message<TResponse>) => {
                if (ack.uuid === messageUUID) {
                    this.bridge?.removeListener(this.name, confirmListener);
                    clearTimeout(t);
                    resolve(ack);
                }
            };
            const t = setTimeout(() => {
                this.bridge?.removeListener(this.name, confirmListener);
                reject('CHANNEL ' + this
                    .name + ', Timeout elapsed' + (messageType ? ' for messageType: ' + MessageType[messageType] : '') + ', UUID: ' + messageUUID);
            }, timeout);

            this.bridge?.addListener(this.name, confirmListener);
        });
    }

    closeChild() {
        this.targetWindow?.close();
    }

    listen<T>(legacyCb?: (message: Message<T>) => void) {
        this.bridge?.addListener(this.name, (message: Message<T>) => {
            if (message.data != null) {
                // Extract custom messageType label if wrapped in metadata
                let messageType = '';
                let actualData: any = message.data;

                if (message.type === MessageType.CustomMessage &&
                    typeof message.data === 'object' &&
                    (message.data as any).__nts_messageType) {
                    messageType = (message.data as any).__nts_messageType;
                    actualData = (message.data as any).__nts_payload;
                } else if (message.type === MessageType.StatusPush) {
                    this.statusPushSubject$.next(message.data as StatusMessageInterface);
                    legacyCb?.(message);
                    return;
                }

                // Emit to Observable Subject for new-pattern listeners
                this.messageSubject$.next(new Message(actualData, message.type, message.uuid));

                // Call original callback for backward compatibility
                legacyCb?.(message);
            }
        });
    }

    /**
     * Send a custom message without expecting confirmation
     * @param data The message data
     * @param messageType Optional message type identifier (used for filtering on receiver)
     */
    sendCustomMessage<T = any>(data: T, messageType: MessageType = MessageType.CustomMessage): void {
        const msgTypeEnum = messageType;
        // Wrap data with metadata if custom messageType label provided for reliable filtering
        const wrappedData = messageType
            ? { __nts_messageType: messageType, __nts_payload: data }
            : data;
        this.bridge?.sendMessage(this.name, new Message(wrappedData, msgTypeEnum, ''));
    }

    /**
     * Send a message and wait for response with confirmation
     * @param data The request data
     * @param messageType Optional message type identifier
     * @param timeout Timeout in ms (default 5000)
     */
    sendMessageAsync<TReq, TRes = any>(data: TReq, messageType: MessageType = MessageType.CustomMessage, timeout = 5000): Promise<TRes> {
        return this.sendWithConfirmAsync<TReq, TRes>(data, messageType, timeout)
            .then(msg => msg.data);
    }

    /**
     * Listen for messages of a specific type as an Observable
     * @param messageType Optional filter by message type. If undefined, receive all messages
     * @returns Observable of message data
     */
    onMessage<T = any>(messageType?: MessageType): Observable<Message<T>> {
        return this.messageSubject$.asObservable().pipe(
            filter(msg => messageType ? msg.type === messageType : true),
            map(msg => new Message<T>(msg.data, msg.type, msg.uuid))
        );
    }

    /**
     * Listen for first message of a specific type, then complete
     * @param messageType Optional filter by message type
     * @returns Promise that resolves with first message data
     */
    async onMessageOnce<T = any>(messageType?: MessageType): Promise<Message<T>> {
        return new Promise<Message<T>>((resolve) => {
            const sub = this.onMessage<T>(messageType).subscribe((data) => {
                sub.unsubscribe();
                resolve(data);
            });
        });
    }

    /**
     * Send a status push from child to parent (typically called by iframe child)
     * @param status The status data to push
     */
    pushStatus(status: StatusMessageInterface): void {
        this.bridge?.sendMessage(this.name, new Message(status, MessageType.StatusPush, ''));
    }

    /**
     * Listen for status push events as an Observable
     * @returns BehaviorSubject of StatusMessageInterface
     */
    onStatusPush(): BehaviorSubject<StatusMessageInterface> {
        return this.statusPushSubject$;
    }

    destroy() {
        window.removeEventListener('unload', this.closeChild);
        this.bridge?.removeAllListeners(this.name);
        this.bridge = null;
        clearInterval(this.pollingInterval);
        // Clean up Observable subjects
        this.messageSubject$.complete();
        this.statusPushSubject$.complete();
    }
}
